import { useCreateListing } from '../_context';
import Toggle from '@ui/Toggle';
import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import { formatCurrency, getDurationInDays, isMobile } from '@util/index';
import { FEE } from '@util/firestore/listing/listing.types';
import Input from '@ui/Input';
import Select from '@ui/Select';
import { useAuth } from 'context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getCauses } from '@util/firestore/products';
import { Divider } from '@ui/Divider';
import { SmartPricingIcon } from 'i/smart-pricing';
import { SmartOffersIcon } from 'i/smart-offers';
import { CreateListingSection } from './CreateListingSection';
import SmartPricingModal from '@c/dashboard/pages/components/SmartPricingModal';
import PriceDropModal from '@c/dashboard/pages/components/PriceDropModal';
import { CreateListingInfoSubsection } from './CreateListingInfoSubSection';

export default function FeaturesSection() {
  const { form, product, variant, checkIncludes, isVehicleListing } =
    useCreateListing();
  const { userDoc } = useAuth();

  const { data: causes = [] } = useQuery({
    queryKey: ['causes'],
    queryFn: getCauses,
  });

  const [smartPricingModalOpen, setSmartPricingModalOpen] = useState(false);
  const [priceDropModalOpen, setPriceDropModalOpen] = useState(false);

  const [isSmartPricing, setIsSmartPricing] = useState(
    Boolean(product.floor_price)
  );
  const [isSmartOffers, setIsSmartOffers] = useState(
    Boolean(product.minimum_offer_price)
  );
  const [isAuction, setIsAuction] = useState(Boolean(product.is_auction));
  const [auction, setAuction] = useState({
    starting_price: product.price ?? '0',
    starting_date: product.start_time
      ? new Date(product.start_time).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    startTimeHour: product.start_time
      ? String(new Date(product.start_time).getHours()).padStart(2, '0')
      : '12',
    startTimeMinute: product.start_time
      ? new Date(product.start_time).getMinutes().toString().padStart(2, '0')
      : '00',
    duration:
      product.start_time && product.end_time
        ? getDurationInDays(product.start_time, product.end_time) + ''
        : '1',
    auctionStart: product.start_time ? product.start_time : 0,
    auctionEnd: product.end_time ? product.end_time : 0,
  });

  useEffect(() => {
    const calcAuctionTimestamps = () => {
      const hasValidAuctionData =
        auction.starting_date &&
        auction.startTimeHour &&
        auction.startTimeMinute;
      if (!hasValidAuctionData) return { start: null, end: null };

      const [year, month, day] = auction.starting_date.split('-');
      const date = new Date(+year, +month - 1, +day);
      const hours = +auction.startTimeHour;
      date.setHours(hours, +auction.startTimeMinute);
      const start = date.getTime();
      const end = start + +auction.duration * 24 * 60 * 60 * 1000;
      return { start, end };
    };

    const { start, end } = calcAuctionTimestamps();
    if (start && end) {
      setAuction((prev) => ({
        ...prev,
        auctionStart: start,
        auctionEnd: end,
      }));
      form.setValue('start_time', start);
      form.setValue('end_time', end);
    }
  }, [
    auction.duration,
    auction.starting_date,
    auction.startTimeHour,
    auction.startTimeMinute,
    form,
  ]);

  const updateFloorPrice = async (floorPrice: number) => {
    setIsSmartOffers(true);
    form.setValue('minimum_offer_price', floorPrice);
  };

  const updateSmartPricing = (floorPrice: number) => {
    setIsSmartPricing(true);
    form.setValue('floor_price', floorPrice);
  };

  const toggleSmartPricing = () => {
    if (isMobile() && !isSmartPricing) {
      setSmartPricingModalOpen(true);
      return;
    }
    setIsSmartPricing((prev) => !prev);
  };
  const toggleSmartOffers = () => {
    if (isMobile() && !isSmartOffers) {
      setPriceDropModalOpen(true);
      return;
    }
    setIsSmartOffers((prev) => !prev);
  };

  if (isVehicleListing) return null;

  return (
    <CreateListingSection title="Features">
      <div className="flex w-full">
        <div className="flex w-full flex-col gap-8">
          {!isAuction && !product.is_auction && (
            <div className="mt-8 flex w-full items-center justify-between gap-x-8 sm:gap-x-4">
              <div className="flex items-center gap-5 md:gap-2">
                <SmartPricingIcon className="size-20 md:size-32" />
                <div className="flex flex-col gap-2">
                  <h3 className="text-body-md font-semibold text-black">
                    Smart Pricing
                  </h3>
                  <p className="hidden text-body-sm font-medium text-text-muted md:block">
                    Increase the chance of a sale by automatically lowering your
                    price gradually over time
                  </p>
                </div>
              </div>

              <Toggle enabled={isSmartPricing} onChange={toggleSmartPricing} />
            </div>
          )}
          {isSmartPricing && !isMobile() && (
            <div className="flex w-full flex-col gap-4">
              <FormLabel
                value="Your product will never go below your floor price"
                errorMessage={form.formState.errors.floor_price?.message}
              >
                <Controller
                  control={form.control}
                  name={'floor_price'}
                  render={({ field: { onChange, onBlur } }) => (
                    <div>
                      <FormCurrencyInputFancy
                        placeholder={'0.00'}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={product.floor_price?.toString() ?? ''}
                      />
                      {!!product.floor_price &&
                        product.category !== 'Race cars' &&
                        variant === 'create-listing' && (
                          <div className="ml-2 mt-[1rem] w-full text-[1.5rem] text-brand-lightest-black">
                            <p>
                              Estimated Earnings:{' '}
                              {formatCurrency(product.floor_price * (1 - FEE))}
                            </p>
                          </div>
                        )}
                    </div>
                  )}
                />
              </FormLabel>
              <span className="text-[1.5rem] text-zinc-700">
                Must be less than the original price
              </span>
            </div>
          )}
          <Divider />
          {!checkIncludes(['race cars']) ? (
            <div className="flex w-full items-center justify-between gap-8 sm:gap-4">
              <div className="flex items-center gap-5 md:gap-2">
                <SmartOffersIcon className="size-20 md:size-32" />
                <div className="flex flex-col gap-2">
                  <h3 className="text-body-md font-semibold text-black">
                    Smart Offers
                  </h3>
                  <p className="hidden text-body-sm font-medium text-text-muted md:block">
                    Automatically reject offers below a certain value
                  </p>
                </div>
              </div>
              <Toggle enabled={isSmartOffers} onChange={toggleSmartOffers} />
            </div>
          ) : null}
          {isSmartOffers && !isMobile() && (
            <div className="flex flex-col gap-4">
              <Controller
                control={form.control}
                name={`minimum_offer_price`}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="max-w-[20rem]">
                    <FormCurrencyInputFancy
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder={'0.00'}
                      defaultValue={value?.toString()}
                      error={Boolean(
                        form.formState.errors?.minimum_offer_price?.message
                      )}
                    />
                  </div>
                )}
              />
              <span className="text-[1.5rem] text-zinc-700">
                Must be less than the original price
              </span>
            </div>
          )}
          {userDoc?.roles?.auctions && (
            <div className="flex w-full items-center justify-between gap-8  sm:gap-4">
              <div className="flex items-center gap-x-4">
                <SmartPricingIcon className="hidden size-32 sm:flex" />

                <div className="flex flex-col gap-y-2">
                  <div className="flex items-center gap-x-4">
                    <h3 className="text-body-md font-semibold">
                      Auction Experience
                    </h3>
                  </div>

                  <p className={'text-zinc-600'}>
                    Making an impact on the sport we all love.
                  </p>
                </div>
              </div>
              <Toggle
                enabled={isAuction}
                onChange={() => {
                  setIsAuction(!isAuction);
                  form.setValue('is_auction', !isAuction);
                  form.setValue('shipment', null);
                  form.setValue('is_flat_rate', true);
                }}
              />
            </div>
          )}
          {isAuction && (
            <>
              {product.is_auction_live && (
                <div className="flex w-full flex-col gap-4">
                  <h3 className="mb-4 text-[1.8rem] text-black">
                    Auction is live, details cannot be changed.
                  </h3>
                </div>
              )}
              <div
                className={`mt-8 flex w-full flex-col gap-y-4 ${
                  product.is_auction_live
                    ? 'pointer-events-none opacity-50'
                    : ''
                }`}
              >
                <h3 className="mb-4 text-[1.8rem] text-black">
                  Fill auction details
                </h3>
                <div className="flex items-start gap-x-12">
                  <div className="flex w-fit flex-col gap-y-4">
                    <label className="text-[1.5rem] font-semibold">
                      Bid Starting Price
                    </label>

                    <div className="flex h-[5rem] items-center">
                      <div className="flex h-full items-center justify-center rounded-bl-2xl rounded-tl-2xl bg-[#F9E8E8] px-8 font-semibold text-brand-secondary">
                        $
                      </div>

                      <input
                        type="text"
                        placeholder="0.00"
                        value={auction.starting_price}
                        onChange={(e) => {
                          const value = e.target.value;
                          const parsedValue = value ? parseFloat(value) : NaN;
                          setAuction((prev) => ({
                            ...prev,
                            starting_price: isNaN(parsedValue)
                              ? 0
                              : parsedValue,
                          }));
                          form.setValue('price', parsedValue);
                        }}
                        className="h-full rounded-br-2xl rounded-tr-2xl border-none bg-[#F4F4F4] px-8 py-2 text-[1.5rem] outline-none"
                      />
                    </div>

                    {form.formState.errors.price && (
                      <span className="text-[1.5rem] text-red-500">
                        {form.formState.errors.price.message}
                      </span>
                    )}
                  </div>
                  <div className="w-fit">
                    <FormLabel value="Start Date" required>
                      <Input
                        type="date"
                        placeholder={'mm/dd/yyyy'}
                        value={auction.starting_date}
                        onChange={(e) => {
                          setAuction((prev) => ({
                            ...prev,
                            starting_date: e.target.value,
                          }));
                        }}
                        className="!rounded-2xl"
                      />
                    </FormLabel>
                  </div>

                  <div className="w-fit">
                    <FormLabel
                      value="Start Time"
                      required
                      errorMessage={form.formState.errors.start_time?.message}
                    >
                      <Input
                        type="time"
                        placeholder="12:00"
                        step={900}
                        value={`${auction.startTimeHour}:${auction.startTimeMinute}`}
                        onChange={(e) => {
                          const [hour, minute] = e.target.value.split(':');
                          setAuction((prev) => ({
                            ...prev,
                            startTimeHour: hour,
                            startTimeMinute: minute,
                          }));
                        }}
                        className="!rounded-2xl"
                      />
                    </FormLabel>
                  </div>
                  <div className="w-fit">
                    <FormLabel value="Auction duration" required>
                      <Select
                        value={auction.duration}
                        onChange={(e) => {
                          setAuction((prev) => ({
                            ...prev,
                            duration: e.value,
                          }));
                        }}
                        options={Array(14)
                          .fill(0)
                          .map((_, i) => ({
                            id: String(i + 1),
                            value: `${i + 1}`,
                            label: `${i + 1}`,
                          }))}
                      />
                    </FormLabel>
                  </div>
                </div>
                <div className="mt-6">
                  <FormLabel value="Select Cause" required>
                    <Controller
                      control={form.control}
                      name="cause"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.value);
                          }}
                          options={[
                            { id: '', value: '', label: 'Select a cause...' },
                            ...causes.map((cause) => ({
                              id: cause.id,
                              value: cause.id,
                              label: cause.name,
                            })),
                          ]}
                          placeholder="Select a cause..."
                        />
                      )}
                    />
                  </FormLabel>
                </div>
              </div>
            </>
          )}
        </div>
        <CreateListingInfoSubsection
          title="Set the best price for the product."
          tips={[
            'Automatically optimize pricing over time',
            'Filter out lowball offers instantly',
            'Save time while keeping control',
          ]}
          hasCheckIcon
        />
      </div>
      <SmartPricingModal
        open={smartPricingModalOpen}
        setOpen={setSmartPricingModalOpen}
        product={product}
        updateSmartPricing={updateSmartPricing}
      />
      <PriceDropModal
        open={priceDropModalOpen}
        setOpen={setPriceDropModalOpen}
        product={product}
        updatePriceDrop={updateFloorPrice}
      />
    </CreateListingSection>
  );
}
